import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, Table, pitchTypeDisplayName, mapCtwb, Checkbox } from 'yankees-react-utils';
import { useOktaQueries, PitcherRepertoire, Team, Advance } from '../../../../data';
import { duplicateNames } from '../../../../utils/formatters';
import CatcherWristbandBatterRow from './CatcherWristbandBatterRow';

const StyledCatcherWristband = styled.div`
    .table {
        text-align: center;
        th {
            white-space: nowrap;
        }
    }
    .ctwbHitterRow {
        height: 47px;
    }
    .playernamedugout {
        font-size: 10.5pt;
        font-weight: bold;
        text-decoration: underline;
    }

    .ciflag1, .ciflag2 {
        color: #FFFFFF;
        padding: 1px;
    }
    .ciflag-1 {
        background-color: #00FF00;
    }
    .ciflag1 {
        background-color: #FFBB00;
    }
    .ciflag2 {
        background-color: #FF0000;
    }
    .ctwbcellbottom {
        position: absolute;
        bottom: 0;
        width: 100%;
    }
    
    .ctwbcelltop {
        position: absolute;
        top: 0;
        width: 100%;
    }

    .lightblue, .darkblue, .lightred, .standardred {
        -webkit-print-color-adjust: exact;
    }
    .lightblue {
        background-color: #AACCFF !important;
    }
    .darkblue {
        background-color: #66AAFF !important;
    }
    .lightred {
        background-color: #F2B9B4 !important;
    }
    .standardred {
        background-color: #E9877F !important;
    }

    .wristbandCell_heatmap {
        min-width: 79px;
    }
    .ctwbBatterImg {
        height: 34.5px;
    }

    /*@media only screen and (max-width:1400px) {
        transform: scale(1.5);
        transform-origin: top;
    }
    @media only screen and (min-width:1400px) {
        transform: scale(1.8);
        transform-origin: top;
    }*/
`;

const mapTeamHitter = ({ Player, SortOrder }, _idx, self) => {
    const hitterName = duplicateNames(Player, self);

    let PNRClass;
    if (!hitterName || hitterName.length <= 5) PNRClass = 'pnrxshort';
    else if (hitterName.length > 9) PNRClass = 'pnrxlong';
    else if (hitterName.length > 7) PNRClass = 'pnrlong';
    else if (hitterName.length === 7) PNRClass = 'pnrmedium';
    else PNRClass = 'pnrshort';

    return { ...Player, SortOrder, PNRClass };
};

const CatcherWristband = ({ dugout, perspective, teamId, vsTeamId, pitcherId, pitcherName, pitcherThrows }) => {
    const { t: translate } = useTranslation();
    const [useSpanish, setUseSpanish] = useState(false);
    const [onePlusNotesHaveSpanish, setOnePlusNotesHaveSpanish] = useState(false);
    const queries = useOktaQueries([
        {
            queryKey: ['team', vsTeamId],
            queryFn: (t) => Team.get(t, vsTeamId),
        },
        {
            queryKey: ['repertoire', pitcherId],
            queryFn: (t) => PitcherRepertoire.get(t, pitcherId),
            select: (resp) => resp.filter((x) => x.Visible).sort((a, b) => a.SortOrder - b.SortOrder || a.FXType - b.FXType),
        },
        {
            queryKey: ['lineup', vsTeamId, true],
            queryFn: (t) => Team.lineup(t, vsTeamId, true),
            select: (resp) => resp.map(mapTeamHitter).sort((a, b) => a.SortOrder - b.SortOrder),
        },
        {
            queryKey: ['ctwb', pitcherId, vsTeamId, teamId],
            queryFn: (t) => Advance.ctwb(t, { pitcherId, vsTeamId, teamId }),
            select: (data) => ({ data, ctwb: mapCtwb(data, pitcherId, translate) }),
        },
    ]);

    const [teamIsLoading, repertoireIsLoading, hittersIsLoading] = queries.map((q) => q.isLoading);
    const [vsTeam, repertoire, hitters, wristband] = queries.map((q) => q.data);
    const numCols = 5 + (repertoire?.length * 2);
    const isCatcherPerspective = perspective === 'catcher';
    const headerRows = (index) => repertoire && (
        <>
            <tr>
                <th rowSpan={2} />
                <th rowSpan={2}>0-0 {translate('sections.opponent.team.abbr-swing-pct')} / {translate('sections.opponent.team.abbr-chase-pct')} / {translate('sections.opponent.team.abbr-miss-pct')}</th>
                <th rowSpan={2} />
                <th colSpan={repertoire.length}>{translate('sections.opponent.team.attack')}</th>
                <th colSpan={repertoire.length}>{translate('sections.opponent.team.two-strikes')}</th>
                <th rowSpan={2} />
                <th rowSpan={2}>
                    {translate('sections.opponent.team.matchup-notes')}
                    {index === -1 && onePlusNotesHaveSpanish === true && (
                        <>
                            <br />
                            <Checkbox
                                id="showspanish"
                                label={translate('sections.opponent.player.show-spanish-notes')}
                                checked={useSpanish}
                                onChange={(e) => setUseSpanish(e.currentTarget.checked)}
                            />
                        </>
                    )}
                </th>
            </tr>
            <tr>
                {repertoire.map((p) => <th key={p.PitchNumber}>{pitchTypeDisplayName({ fxtype: p.FXType, fxsubtype: p.FXSubType }, true, true, translate)}</th>)}
                {repertoire.map((p) => <th key={p.PitchNumber}>{pitchTypeDisplayName({ fxtype: p.FXType, fxsubtype: p.FXSubType }, true, true, translate)}</th>)}
            </tr>
        </>
    );
    return (
        <StyledCatcherWristband>
            {(teamIsLoading || repertoireIsLoading || hittersIsLoading) && <LoadingSpinner />}
            {repertoire && hitters && vsTeam && (
                <Table>
                    <thead>
                        <tr>
                            <th className="table-title" colSpan={numCols}>
                                {pitcherName} {translate('baseball.abbr-versus')} {vsTeam.City || vsTeam.Abbr}
                                {isCatcherPerspective ? ` (${translate('sections.opponent.team.catcher-perspective')})` : ''}
                                {wristband?.data?.heatmapupdated.length > 0 && wristband?.data?.heatmapupdated[0].venuename !== null ? ` (${wristband?.data?.heatmapupdated[0].venuename})` : ''}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {headerRows(-1)}
                        {hitters.map((hitter, idx) => (
                            <React.Fragment key={hitter.PlayerID}>
                                <CatcherWristbandBatterRow
                                    name={duplicateNames(hitter, hitters)}
                                    bats={hitter.Bats}
                                    throws={pitcherThrows}
                                    pitcherId={pitcherId}
                                    batterId={hitter.PlayerID}
                                    batterNameClass={dugout ? 'playernamedugout' : `playernamerotate ${hitter.PNRClass}`}
                                    repertoire={repertoire}
                                    ctwb={wristband?.ctwb}
                                    data={wristband?.data}
                                    isCatcherPerspective={isCatcherPerspective}
                                    vsLevelId={(vsTeam?.LevelID || vsTeam?.Level?.LevelID)}
                                    useSpanish={useSpanish}
                                    noteHasSpanishCallback={() => setOnePlusNotesHaveSpanish(true)} // this only returns when there is spanish data
                                />
                                {idx % 5 === 4 && (
                                    <>
                                        <tr height={27}>
                                            <td colSpan={numCols} />
                                        </tr>
                                        {headerRows(idx)}
                                    </>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </Table>
            )}
        </StyledCatcherWristband>
    );
};

CatcherWristband.propTypes = {
    teamId: PropTypes.number.isRequired,
    vsTeamId: PropTypes.number.isRequired,
    pitcherId: PropTypes.number.isRequired,
    pitcherName: PropTypes.string.isRequired,
    pitcherThrows: PropTypes.number.isRequired,
    dugout: PropTypes.bool,
    perspective: PropTypes.string,
};
CatcherWristband.defaultProps = {
    dugout: true,
    perspective: 'pitcher',
};

export default CatcherWristband;
